import { colorTableOperationIcon } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Button, Flex } from 'antd';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import * as Icon from 'react-feather';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import {
  CreateTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  getFormTableFieldName,
  TableOperationButton,
  TanstackFormTable,
} from '../../common/form';
import {
  createCloneParam,
  isCloneParam,
  parseCloneParam,
} from '../../common/queryParams/clone';
import { CREATE_PARAM, isCreateParam } from '../../common/queryParams/create';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { useDataObjectContext } from '../DataObjectContext';
import {
  AlternativeAlgorithmSetDataType,
  DataObjectValidFormDataType,
} from '../schema';
import translations from '../translations/AlternativeAlgorithmsEditTable.translations.json';
import { DataObjectFormDataType } from '../types';
import { usePanelParams } from '../usePanelParams';
import { useTableFilter } from '../useTableFilter';
import { AlternativeAlgorithmEditPanel } from './AlternativeAlgorithmEditPanel';
import { AlternativesFormCell } from './AlternativesCell';
import {
  alternativeFilterFn,
  createDefaultAlternativeSet,
  getExceptionsCount,
} from './utils';
import { ViewableGroupOrAlgorithm } from './types';
import { useListAlgorithmsQuery } from '../algorithms/query/useGetAlgorithmsQuery';
import { AlternativeAlgorithmViewModal } from './AlternativeAlgorithmViewModal';

const { addTranslations, useTranslation } = i18nNsFactory(
  'alternatives-edit-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<AlternativeAlgorithmSetDataType>();

export function AlternativeAlgorithmsEditTable() {
  const { t } = useTranslation();

  const [viewableGroupOrAlgorithm, setViewableGroupOrAlgorithm] = useState<
    ViewableGroupOrAlgorithm | undefined
  >();
  const { setValue, getValues } = useFormContext<DataObjectFormDataType>();
  const dataObjectVersionId = getValues('versionId');
  const { data: algorithms = [] } = useListAlgorithmsQuery({
    dataObjectVersionId,
    isAlgorithmView: true,
  });
  const { setAlternativeFilters } = useDataObjectContext();
  const { columnFilters, setColumnFilters } = useTableFilter(
    setAlternativeFilters,
  );

  const [
    { alternativeAlgorithmSetId, alternativeAlgorithmSetIndex },
    setPanelParams,
  ] = usePanelParams();

  const getMaxCode = useCallback(
    () =>
      getValues('alternativeAlgorithmSets').reduce(
        (acc, alternative) => (acc > alternative.code ? acc : alternative.code),
        0,
      ),
    [getValues],
  );

  const [editableIndex, editableAlternative] = useMemo(() => {
    if (alternativeAlgorithmSetId) {
      if (isCreateParam(alternativeAlgorithmSetId)) {
        return [undefined, createDefaultAlternativeSet(getMaxCode() + 1)];
      } else {
        if (isCloneParam(alternativeAlgorithmSetId)) {
          const cloneId = parseCloneParam(alternativeAlgorithmSetId);
          return [
            undefined,
            getValues('alternativeAlgorithmSets').find(
              ({ id }) => id === cloneId,
            ),
          ];
        } else {
          return (
            Array.from(getValues('alternativeAlgorithmSets').entries()).find(
              ([, { id }]) => id === Number(alternativeAlgorithmSetId),
            ) || []
          );
        }
      }
    } else {
      if (isCloneParam(alternativeAlgorithmSetIndex)) {
        return [
          undefined,
          getValues('alternativeAlgorithmSets')[
            parseCloneParam(alternativeAlgorithmSetIndex)
          ],
        ];
      } else {
        const index = Number(alternativeAlgorithmSetIndex);
        return [index, getValues('alternativeAlgorithmSets')[index]];
      }
    }
  }, [
    getMaxCode,
    getValues,
    alternativeAlgorithmSetId,
    alternativeAlgorithmSetIndex,
  ]);

  const addRow = useCallback(() => {
    setPanelParams({ alternativeAlgorithmSetId: CREATE_PARAM });
  }, [setPanelParams]);

  const submit = useCallback(
    (alternative: AlternativeAlgorithmSetDataType, index?: number) => {
      if (index !== undefined) {
        setValue(`alternativeAlgorithmSets.${index}`, alternative, {
          shouldDirty: true,
        });
      } else {
        setValue(
          `alternativeAlgorithmSets`,
          getValues('alternativeAlgorithmSets').concat(alternative),
          { shouldDirty: true },
        );
      }
      setPanelParams();
    },
    [setValue, getValues, setPanelParams],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: t('column.code.label'),
        cell: CodeCell,
        meta: {
          width: 100,
          className: 'sticky-left',
        },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('name', {
        header: t('column.name.label'),
        filterFn: 'includesString',
        meta: {
          width: 250,
        },
      }),
      columnHelper.accessor('alternativeAlgorithms', {
        header: t('column.included.label'),
        filterFn: alternativeFilterFn,
        cell: (props) => (
          <AlternativesFormCell
            rowIndex={props.row.index}
            onSelectGroupOrAlgorithm={setViewableGroupOrAlgorithm}
          />
        ),
        meta: {
          width: 250,
        },
      }),
      columnHelper.accessor(getExceptionsCount, {
        id: 'exceptionsCount',
        header: t('column.exceptions.label'),
        filterFn: 'includesString',
        cell: ExceptionsCountCell,
        meta: {
          width: 100,
          textAlign: 'right',
        },
      }),

      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <CreateTableRowButton
              title={t('button.add-alternative')}
              onClick={addRow}
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: OperationsCell,
        meta: {
          className: 'sticky-right',
          width: 85,
          minWidth: 85,
        },
      }),
    ],
    [addRow, getValues, t],
  );

  return (
    <TableWrapper vertical align="end" gap="middle">
      <TanstackFormTable
        name="alternativeAlgorithmSets"
        columns={columns}
        filters={columnFilters}
        onFilter={setColumnFilters}
        defaultSort={[{ id: 'code', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {editableAlternative && (
        <AlternativeAlgorithmEditPanel
          algorithms={algorithms}
          editableAlternative={editableAlternative}
          index={editableIndex}
          onClose={() => setPanelParams()}
          onSubmit={submit}
        />
      )}
      {viewableGroupOrAlgorithm && (
        <AlternativeAlgorithmViewModal
          algorithms={algorithms}
          viewable={viewableGroupOrAlgorithm}
          onClose={() => setViewableGroupOrAlgorithm(undefined)}
          isEditMode={true}
        />
      )}
    </TableWrapper>
  );
}

const CodeCell: FC<CellContext<AlternativeAlgorithmSetDataType, any>> = memo(
  (props) => {
    const [, setPanelParams] = usePanelParams();
    const alternativeAlgorithmSetId = props.row.original.id;
    const alternativeAlgorithmSetIndex = props.row.index;
    const params = alternativeAlgorithmSetId
      ? { alternativeAlgorithmSetId }
      : { alternativeAlgorithmSetIndex };
    const href = useHrefWithQueryParams(params);
    return (
      <FormField
        name={getFormTableFieldName(props)}
        preview={{
          previewOnly: true,
          render: (value) => (
            <StyledButton
              id={`edit-alternative-${props.row.index}`}
              href={href}
              onClick={suppressHref(() => setPanelParams(params))}
              shape="round"
              type="link"
            >
              {value}
            </StyledButton>
          ),
        }}
      />
    );
  },
);

const ExceptionsCountCell: FC<
  CellContext<AlternativeAlgorithmSetDataType, any>
> = memo((props) => {
  const { getValues } = useFormContext<DataObjectValidFormDataType>();
  const row = getValues(`alternativeAlgorithmSets.${props.row.index}`);
  const count = getExceptionsCount(row);
  return count ? count.toString() : '';
});

const OperationsCell: FC<CellContext<AlternativeAlgorithmSetDataType, any>> =
  memo((props) => {
    const { t } = useTranslation();
    const [, setPanelParams] = usePanelParams();
    const alternativeAlgorithmSetId = props.row.original.id;
    const alternativeAlgorithmSetIndex = props.row.index;
    const params = alternativeAlgorithmSetId
      ? {
          alternativeAlgorithmSetId: createCloneParam(
            alternativeAlgorithmSetId,
          ),
        }
      : {
          alternativeAlgorithmSetIndex: createCloneParam(
            alternativeAlgorithmSetIndex,
          ),
        };
    return (
      <Flex justify="center" gap="small">
        <TableOperationButton
          id={`alternatives-clone-${props.row.index}`}
          title={t('button.clone')}
          icon={<Icon.Copy size={18} color={colorTableOperationIcon} />}
          type="text"
          href={useHrefWithQueryParams(params)}
          onClick={suppressHref(() => setPanelParams(params))}
          aria-label={t('button.clone')}
        />
        <DeleteFormTableRowButton context={props} />
      </Flex>
    );
  });

const StyledButton = styled(Button)`
  margin-block: -0.5em;
  padding-inline: 0.75em;
`;

export const TableWrapper = styled(Flex)`
  margin: auto;
`;
